/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-01-18 09:04:34
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { FullStory, init } from '@fullstory/browser';
import queryString from 'query-string';
import moment from 'moment';

import { integrations } from '../config';
import {
  SemanticSearch,
  ProductPerformanceRanking,
  SearchPersonalization
} from 'src/models/search';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';

const getBoostPlan = (tenantApp: Object): string => {
  return _.get(tenantApp, 'selectedPlan', '').replace('_discount', '') || 'Trial';
};

const buildDataEventEditISW = (data: FilterSettingsType): Record<String, any> => {
  const suggestionBlocks = _.get(data, 'search.suggestionBlocks', []);
  const productsBlock = suggestionBlocks.find(
    suggestionBlock => suggestionBlock.type === 'products'
  );
  const collectionsBlock = suggestionBlocks.find(
    suggestionBlock => suggestionBlock.type === 'collections'
  );
  const suggestionsBlock = suggestionBlocks.find(
    suggestionBlock => suggestionBlock.type === 'suggestions'
  );
  const pagesBlock = suggestionBlocks.find(suggestionBlock => suggestionBlock.type === 'pages');

  return {
    disProduct: !!(_.get(productsBlock, 'status', 'active') === 'active'),
    disCollection: !!(_.get(collectionsBlock, 'status', 'active') === 'active'),
    disPopularSuggestions: !!(_.get(suggestionsBlock, 'status', 'active') === 'active'),
    disBlogAndPage: !!(_.get(pagesBlock, 'status', 'active') === 'active'),
    disSearchTermSuggest: _.get(data, 'search.suggestionNoResult.search_terms.status', false),
    disPopularProducts: _.get(data, 'search.suggestionNoResult.products.status', false),
    disRecentSearches: _.get(data, 'search.searchBoxOnclick.recentSearch.status', true),
    disSearchTermSuggestInSearchBox: _.get(
      data,
      'search.searchBoxOnclick.searchTermSuggestion.status',
      false
    ),
    disPopularProductsInSearchBox: _.get(
      data,
      'search.searchBoxOnclick.productSuggestion.status',
      false
    )
  };
};

const flattenObj = ob => {
  let result = {};

  for (const i in ob) {
    if (typeof ob[i] === 'object' && !Array.isArray(ob[i])) {
      const temp = flattenObj(ob[i]);
      for (const j in temp) {
        result[i + '.' + j] = temp[j];
      }
    } else {
      result[i] = ob[i];
    }
  }
  return result;
};

const expandObj = (target, keySeparator) => {
  var result = {};
  for (var key in target) {
    if (target.hasOwnProperty(key)) {
      var nestedKeys = key.split(keySeparator);
      // Get the last subKey
      var leaf = nestedKeys[nestedKeys.length - 1];
      // Get all subKeys except for the last
      var branch = nestedKeys.slice(0, nestedKeys.length - 1);

      var currentTarget = result;
      for (var i = 0; i < branch.length; i += 1) {
        var subKey = nestedKeys[i];
        // If this is the first time visiting this branch, we need to instantiate it
        if (currentTarget[subKey] === undefined) {
          currentTarget[subKey] = {};
        }
        // Visit the branch
        currentTarget = currentTarget[subKey];
      }
      currentTarget[leaf] = target[key];
    }
  }
  return result;
};

// Init Full Story
const initFullStory = () => {
  const queries = queryString.parse(window.location.search);
  // If having auth0Token => access via app simulator
  const { auth0Token, shop = '' } = queries;
  const isSimulator = auth0Token ? true : false;

  init({
    orgId: integrations.fullStory.orgId,
    debug: integrations.fullStory.debug || isSimulator,
    devMode: integrations.fullStory.devMode,
    recordCrossDomainIFrames: true,
    recordOnlyThisIFrame: true
  });

  if (shop) {
    FullStory('setIdentity', {
      uid: shop
    });
  }
};

/**
 * Set user property from tenant
 */
const setupFullStoryUser = tenantApp => {
  const plan = getBoostPlan(tenantApp);
  const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);
  let userProperty = {
    displayName: tenantApp.externalInfo?.name,
    tenantId: tenantApp.tenantId,
    boostPlan: plan,
    shopifyPlan: _.get(tenantApp, 'externalInfo.plan_display_name', ''),
    productCount: tenantApp.appParams?.currentTotalProducts,
    name: tenantApp.externalInfo?.name,
    language: tenantApp.appLanguage,
    country: tenantApp.externalInfo?.country_name,
    devStore: _.startsWith(tenantApp.tenantId, 'bc-dev-v2'),
    ssrEnable: isEnableShopifyIntegration
  };

  // Check active tenant
  if (plan === 'Trial') {
    const trialExpiresAt = _.get(tenantApp, 'trialExpiresAt', 0);
    userProperty.trialEndDate = moment.utc(trialExpiresAt).format('YYYY-MM-DD hh:mm:ss A');
  } else {
    const settings = _.get(tenantApp, 'settings', {});
    userProperty = {
      ...userProperty,
      // activeDays: '',
      subscribeAISearch: _.get(settings, 'instantSearch.settings.AI-synonyms', false),
      subscribeRecommendation: _.get(
        settings,
        'recommendation.settings.frequently-bought-together',
        false
      ),
      subscribeAnalytics90: _.get(settings, 'analyticV2.settings.analytics-90-days', false),
      subscribeAnalytics180: _.get(settings, 'analyticV2.settings.analytics-180-days', false),
      subscribeAnalytics365: _.get(settings, 'analyticV2.settings.analytics-365-days', false),
      subscribeMerchandising: _.get(
        settings,
        'visualMerchandising.settings.collection-page',
        false
      ),
      subscribeVariantDisplay: _.get(settings, 'variantsDisplay.settings.variant-display', false)
    };
  }

  FullStory('setIdentity', {
    uid: tenantApp.tenantId,
    properties: userProperty
  });
};

/**
 * Track event
 */
const trackFSEvent = (name, properties) => {
  FullStory('trackEvent', { name, properties });
};

/**
 * Track specific events
 */
const trackFSEvent_UpdateFilterSettings = (type: String, data: Record<String, any>): void => {
  if (type === 'filterLayout') {
    trackFSEvent('Change Filter Layout', {
      feature: 'Filter Layout',
      verticalLayout: data?.general?.filterTreeVerticalStyle,
      horizontalLayout: data?.general?.filterTreeHorizontalStyle,
      mobileLayout: data?.general?.filterTreeMobileStyle,
      stickyOnMobile: data?.general?.stickyFilterOnMobile,
      stickyOnDesktop: data?.general?.stickyFilterOnDesktop
    });
  }
  if (type === 'filterSettings') {
    trackFSEvent('Change Filter Settings', {
      feature: 'Filter Settings'
    });
  }
  if (type === 'instantSearchDisplay') {
    const layoutSettingsMapping = {
      style1: 'One Column',
      style2: 'Default - Two Columns',
      style3: 'Overlay Fullwidth'
    };
    // Tracking when enable toggle instant search
    if (data.isEnableSuggestionTracking) {
      trackFSEvent('Enable Instant Search ', {
        feature: 'Instant Search Display',
        layoutSettings: layoutSettingsMapping[data?.suggestionStyle]
      });
      return;
    }
    // Tracking when edit instant search
    if (data?.search?.enableSuggestion) {
      const dataTracking = buildDataEventEditISW(data);
      trackFSEvent('Edit Instant Search Display', {
        feature: 'Instant Search Display',
        ...dataTracking
      });
    } else {
      // Tracking when disabled instant search
      trackFSEvent('Disable Instant Search', {
        feature: 'Instant Search Display',
        layoutSettings: layoutSettingsMapping[data?.search?.suggestionStyle]
      });
    }
  }
  if (type === 'searchPageDisplay') {
    trackFSEvent('Edit Search Results Page', {
      feature: 'Search Page Display',
      disResultCollection: data?.search?.searchPanelBlocks?.collections?.active,
      disResultPages: data?.search?.searchPanelBlocks?.pages?.active,
      disSearchTips: data?.search?.searchPanelBlocks?.searchTips?.active,
      disSearchTermSuggest: data?.search?.searchPanelBlocks?.searchTermSuggestions?.active,
      disPopularProducts: data?.search?.searchPanelBlocks?.mostPopularProducts?.active
    });
  }
  if (type === 'translation') {
    trackFSEvent('Change Translation', {
      feature: 'Translation'
    });
  }
};

const trackFSEvent_SelectAnalyticsFrame = () => {
  const path = window.location.pathname;
  const pathNames = path.substring(1).split('/');
  trackFSEvent('Select Analytics Frame', {
    feature: 'Analytics',
    analyticsType: pathNames?.[0],
    subType: pathNames?.[1] || 'overview'
  });
};

const trackFSEvent_UpdateRelevanceSearchSetting = (
  currentSearchSetting: SearchSetting,
  newSearchSetting: SearchSetting
) => {
  // Search fields settings
  const currentSearchFields = currentSearchSetting.searchFields.sort((a, b) => a.name - b.name);
  const newSearchFields = newSearchSetting.searchFields.sort((a, b) => a.name - b.name);
  for (let i = 0; i < currentSearchFields.length; i++) {
    if (currentSearchFields[i].status !== newSearchFields[i].status) {
      const trackingSearchField = {};
      newSearchFields.forEach(e => {
        trackingSearchField[e.name] = e?.searchWeight || 'non-searchable field';
      });
      trackFSEvent('Edit Relevance Settings', {
        feature: 'Standard Search',
        ...trackingSearchField
      });
      break;
    }
  }
};

const trackFSEvent_SemanticSearch = (oldSettings: SemanticSearch, settings: SemanticSearch) => {
  if (oldSettings?.enable === false && settings?.enable === true) {
    trackFSEvent('Enable Semantic Search', {
      feature: 'Semantic Search'
    });
  }

  if (oldSettings?.enable === true && settings?.enable === false) {
    trackFSEvent('Disable Semantic Search', {
      feature: 'Semantic Search'
    });
  }
};

const trackFSEvent_ProductPerformanceRanking = (
  oldSettings: ProductPerformanceRanking,
  settings: ProductPerformanceRanking
) => {
  if (oldSettings?.enabled !== true && settings?.enabled === true) {
    trackFSEvent('Enable Product Performance Ranking', {
      feature: 'Product performance ranking'
    });
  }

  if (oldSettings?.enabled !== false && settings?.enabled === false) {
    trackFSEvent('Disable Product Performance Ranking', {
      feature: 'Product performance ranking'
    });
  }

  const { numberReordered = 0 } = oldSettings;
  const { numberReordered: newNumberReordered = 0 } = settings;
  if (parseInt(numberReordered) !== parseInt(newNumberReordered)) {
    trackFSEvent('Adjust Product Number Ranking', {
      feature: 'Product performance ranking',
      productLimits: settings?.numberReordered
    });
  }
};

const trackFSEvent_SearchPersonalization = (
  oldSettings: SearchPersonalization,
  settings: SearchPersonalization
) => {
  if (oldSettings?.enabled !== true && settings?.enabled === true) {
    trackFSEvent('Enable Search Personalization', {
      feature: 'Search personalization'
    });
  }

  if (oldSettings?.enabled !== false && settings?.enabled === false) {
    trackFSEvent('Disable Search Personalization', {
      feature: 'Search personalization'
    });
  }

  const { numberReordered = 0 } = oldSettings;
  const { numberReordered: newNumberReordered = 0 } = settings;
  if (parseInt(numberReordered) !== parseInt(newNumberReordered)) {
    trackFSEvent('Adjust Personalized Items Limitations', {
      feature: 'Search personalization',
      productLimits: settings?.numberReordered
    });
  }
};

const trackFSEvent_CreateMerchandiseRule = (payload: CreateMerchandiseRulePayload = {}) => {
  // const EVENT_NAME_MAPPING = {
  //   pin: 'Create a Pin Rule',
  //   boost: 'Create a Boost Rule',
  //   demote: 'Create a Demote Rule',
  //   hide: 'Create a Hide Rule',
  //   filter: 'Create a Filter Rule'
  // };
  // const strategyType = payload.strategies?.[0]?.type;
  // // Strategy: pin
  // if (strategyType === 'pin') {
  //   trackFSEvent(EVENT_NAME_MAPPING[strategyType], {
  //     feature: 'Merchandising V2',
  //     pageType: payload.ruleType,
  //     selectedStrategy: strategyType,
  //     selectedMatchPin: payload.strategies?.[0]?.pinMatchRequired,
  //     numberPinProducts: payload?.strategies?.[0]?.pinPositions?.filter(e => e.id)?.length
  //   });
  //   return;
  // }
  // // Other strategy
  // const trackingAttributes: string[] = [];
  // for (const strategy of payload.strategies) {
  //   (strategy.attributes || []).forEach(e => {
  //     trackingAttributes.push(e.attribute);
  //   });
  // }
  // trackFSEvent(EVENT_NAME_MAPPING[strategyType], {
  //   feature: 'Merchandising V2',
  //   pageType: payload.ruleType,
  //   selectedStrategy: strategyType,
  //   attributes: trackingAttributes,
  //   numberAttributes: trackingAttributes.length
  // });
  const data = {
    feature: 'Merchandising V2',
    pageType: payload?.ruleType,
    selectedStrategies: payload?.strategies,
    numberStrategies: payload?.strategies?.length
  };
  if (_.has(payload, 'isDisabledPerformanceRanking')) {
    _.set(data, 'isDisabledPerformanceRanking', payload?.isDisabledPerformanceRanking);
  }
  trackFSEvent('Create a merchandising rule', data);
};

const trackFSEvent_ChangeThemeElement = (
  oldSetting: Record<String, any>,
  newSetting: Record<String, any>,
  themeName: string,
  addedElements: Record<String, any>[],
  removedElements: Record<String, any>[]
): void => {
  const flattenOldSetting = flattenObj(oldSetting);
  const flattenNewSetting = flattenObj(newSetting);
  const flattenResult = {};

  for (const key of Object.keys(flattenNewSetting)) {
    if (_.isNil(flattenOldSetting[key])) {
      flattenResult[key] = { type: 'new', value: flattenNewSetting[key] };
      continue;
    }

    if (flattenOldSetting[key] !== flattenNewSetting[key]) {
      flattenResult[key] = {
        type: 'change',
        from: flattenOldSetting[key],
        to: flattenNewSetting[key]
      };
    }
  }

  for (const key of Object.keys(flattenOldSetting)) {
    if (_.isNil(flattenNewSetting[key])) {
      flattenResult[key] = { type: 'remove', value: flattenOldSetting[key] };
    }
  }
  const expandResult = expandObj(flattenResult, '.');
  for (const key of Object.keys(expandResult)) {
    // check element is already created event
    let element = _.find(addedElements, item => item.elementName === key) || {};
    if (_.isEmpty(element))
      element = _.find(removedElements, item => item.elementName === key) || {};

    if (_.isEmpty(element)) {
      trackFSEvent('Change element settings', {
        feature: 'Visual Editor',
        elementName: key,
        selectedTheme: themeName,
        dataChange: expandResult[key]
      });
    }
  }
};

const trackFSEvent_ChangeSSRVisualEditor = (
  selectedTemplate: string,
  templateId: string,
  settings: Object,
  editedWidgets: Object
): void => {
  const { colors = {}, typography = {} } = settings;
  const baseColors = [
    colors['baseColor1'] || '',
    colors['baseColor2'] || '',
    colors['baseColor3'] || '',
    colors['baseColor4'] || '',
    colors['baseColor5'] || ''
  ];
  const accentColors = [
    colors['accentColor1'] || '',
    colors['accentColor2'] || '',
    colors['accentColor3'] || '',
    colors['accentColor4'] || ''
  ];
  const typographyFont = [
    _.get(typography, 'headings.fontFamily', ''),
    _.get(typography, 'body.fontFamily', ''),
    _.get(typography, 'buttons.fontFamily', ''),
    _.get(typography, 'linkAccent.fontFamily', '')
  ];

  const widgetNames = _.map(editedWidgets, widget => widget.widgetName);

  // Tracking style settings
  trackFSEvent('Edit Style Settings', {
    feature: 'SSR Visual Editor',
    selectedTemplate,
    templateId,
    baseColors,
    accentColors,
    typography: typographyFont
  });

  // Tracking change code editor
  if (!_.isEmpty(widgetNames)) {
    trackFSEvent('Edit code editor', {
      feature: 'SSR Visual Editor',
      selectedTemplate,
      templateId,
      widgetNames
    });
  }
};
export {
  initFullStory,
  setupFullStoryUser,
  trackFSEvent,
  trackFSEvent_UpdateFilterSettings,
  trackFSEvent_SelectAnalyticsFrame,
  trackFSEvent_UpdateRelevanceSearchSetting,
  trackFSEvent_SemanticSearch,
  trackFSEvent_ProductPerformanceRanking,
  trackFSEvent_SearchPersonalization,
  trackFSEvent_CreateMerchandiseRule,
  trackFSEvent_ChangeThemeElement,
  trackFSEvent_ChangeSSRVisualEditor
};
